import React, { useState } from 'react';
import styles from './FeeInfoModal.module.scss';
import { OutsideAlerter } from '../TipPublish/shared/OutsideAlerter/OutsideAlerter';
import { MONTH_NAMES } from '../TipPublish/utils/buildCustomDate';
//assets
import SVG from 'react-inlinesvg';
import closeIcon from '../../assets/icons/close.svg';
import { toProperCase } from '../TipPublish/utils/toProperCase';

//Tr
import { useTranslation } from 'react-i18next';
import { tr } from '../../utils/translations/translator';
import { FeeTiers, SalesSummary } from '../../sagas/dashboard/types';

interface IFeeInfoModalProps {
  userName: any;
  userCurrency: any;
  tipsterFeeTiers: FeeTiers;
  salesSummary: SalesSummary;
  setIsFeeInfoModalOpen: (v: boolean) => void;
  setIsFeeTierModalOpen: (v: boolean) => void;
}

export const FeeInfoModal: React.FC<IFeeInfoModalProps> = ({
  userName,
  tipsterFeeTiers,
  userCurrency,
  salesSummary,
  setIsFeeInfoModalOpen,
  setIsFeeTierModalOpen,
}) => {
  const now = new Date();
  const { t } = useTranslation();

  return (
    <div className={styles.feeInfoModal}>
      <OutsideAlerter onBlur={() => setIsFeeInfoModalOpen(false)}>
        {/* DESKTOP */}
        <div className={styles.boxDesktop}>
          <SVG
            src={closeIcon}
            className={styles.close}
            onClick={() => setIsFeeInfoModalOpen(false)}
          />

          <div className={styles.border}>
            <span className={styles.date}>
              {tr(t, MONTH_NAMES[now.getMonth()]).toUpperCase()}{' '}
              {now.getFullYear()}
            </span>

            <div className={styles.topContent}>
              <div className={styles.left}>
                <span className={styles.tierHeader}>
                  {toProperCase(
                    tipsterFeeTiers
                      .getCurrentTier(userCurrency)
                      .code.replace('_', ' '),
                  )}
                </span>

                <span className={styles.tierSubheader}>
                  {t('you_are_already_tier', {
                    name: userName,
                    fee_tier: toProperCase(
                      tipsterFeeTiers
                        .getCurrentTier(userCurrency)
                        .code.replace('_', ' '),
                    ),
                  })}
                </span>

                <span className={styles.description}>
                  {tr(t, 'fee_tier_info_reminder')}
                </span>
              </div>

              <div
                className={styles.right}
                style={{
                  backgroundColor:
                    tipsterFeeTiers.getCurrentColor(userCurrency),
                }}
              >
                <img
                  src={tipsterFeeTiers.getCurrentTier(userCurrency).iconURL}
                  className={styles.logoImg}
                />
              </div>
            </div>

            <div className={styles.bottomContent}>
              <div className={styles.rankProgressBox}>
                <div className={styles.topValues}>
                  <span className={styles.currentValue}>
                    {salesSummary.currentMonthGrossRevenue.toString()}
                  </span>
                  <span className={styles.goalValue}>
                    /{' '}
                    {tipsterFeeTiers
                      .getCurrentTier(userCurrency)
                      .values[userCurrency].to.toString()}
                  </span>
                </div>

                <div className={styles.separator} />

                <div className={styles.bottomValues}>
                  <span className={styles.currentValue}>
                    { 100 -
                      tipsterFeeTiers.getCurrentTier(userCurrency)
                        .tipsterFeePercentage
                    }
                    %
                  </span>
                  <span className={styles.goalValue}>{tr(t, 'benefit')}</span>
                </div>
              </div>

              <div className={styles.nextRankInfoBox}>
                <div className={styles.infoBox}>
                  <div className={styles.topRow}>
                    <span>{tr(t, 'next').toUpperCase()}</span>
                    <span>
                      {tipsterFeeTiers
                        .getNextTier(userCurrency)
                        .formatFeeRange(userCurrency)}
                    </span>
                  </div>

                  <div className={styles.bottomRow}>
                    <span className={styles.tierLevel}>
                      {toProperCase(
                        tipsterFeeTiers
                          .getNextTier(userCurrency)
                          .code.replace('_', ' '),
                      )}
                    </span>

                    <span className={styles.tierBenefit}>
                      {t('of_benefit', {
                        fee_percentage:
                         100 - tipsterFeeTiers.getNextTier(userCurrency)
                            .tipsterFeePercentage,
                      })}
                    </span>
                  </div>
                </div>

                <div
                  className={styles.tierButton}
                  onClick={() => {
                    setIsFeeInfoModalOpen(false);
                    setIsFeeTierModalOpen(true);
                  }}
                >
                  {toProperCase(tr(t, 'see_all_fee_tiers'))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* MOBILE */}
        <div className={styles.boxMobile}>
          <SVG
            src={closeIcon}
            className={styles.close}
            onClick={() => setIsFeeInfoModalOpen(false)}
          />

          <div className={styles.headerRow}>
            <div className={styles.left}>
              <span className={styles.date}>
                {tr(t, MONTH_NAMES[now.getMonth()]).toUpperCase()}{' '}
                {now.getFullYear()}
              </span>

              <span className={styles.tierHeader}>
                {toProperCase(
                  tipsterFeeTiers
                    .getCurrentTier(userCurrency)
                    .code.replace('_', ' '),
                )}
              </span>
            </div>

            <div
              className={styles.right}
              style={{
                backgroundColor: tipsterFeeTiers.getCurrentColor(userCurrency),
              }}
            >
              <img
                src={tipsterFeeTiers.getCurrentTier(userCurrency).iconURL}
                className={styles.logoImg}
              />
            </div>
          </div>

          <span className={styles.tierSubheader}>
            {t('you_are_already_tier', {
              name: userName,
              fee_tier: toProperCase(
                tipsterFeeTiers
                  .getCurrentTier(userCurrency)
                  .code.replace('_', ' '),
              ),
            })}
          </span>

          <div className={styles.currentTierBox}>
            <div className={styles.currentRevenue}>
              <span className={styles.currentValue}>
                {salesSummary.currentMonthGrossRevenue.toString()}
              </span>

              <span className={styles.goalValue}>
                /{' '}
                {tipsterFeeTiers
                  .getCurrentTier(userCurrency)
                  .values[userCurrency].to.toString()}
              </span>
            </div>

            <div className={styles.separator} />

            <div className={styles.currentBenefit}>
              <span className={styles.currentValue}>
                {
                  100 - tipsterFeeTiers.getCurrentTier(userCurrency)
                    .tipsterFeePercentage
                }
                %
              </span>
              <span className={styles.goalValue}>{tr(t, 'benefit')}</span>
            </div>
          </div>

          <div className={styles.nextTierBox}>
            <div className={styles.topRow}>
              <span>{tr(t, 'next').toUpperCase()}</span>
              <span>
                {tipsterFeeTiers
                  .getNextTier(userCurrency)
                  .formatFeeRange(userCurrency)}
              </span>
            </div>
            <div className={styles.bottomRow}>
              <span className={styles.tierLevel}>
                {toProperCase(
                  tipsterFeeTiers
                    .getNextTier(userCurrency)
                    .code.replace('_', ' '),
                )}
              </span>

              <span className={styles.tierBenefit}>
                {t('of_benefit', {
                  fee_percentage:
                    100 - tipsterFeeTiers.getNextTier(userCurrency)
                      .tipsterFeePercentage,
                })}
              </span>
            </div>
          </div>

          <div
            className={styles.tierButton}
            onClick={() => {
              setIsFeeInfoModalOpen(false);
              setIsFeeTierModalOpen(true);
            }}
          >
            {toProperCase(tr(t, 'see_all_fee_tiers'))}
          </div>
        </div>
      </OutsideAlerter>
    </div>
  );
};
